/// <amd-module name="Test/ManageTestPlans/Sources/TestPlanFavoritesSource" />


import { Favorite, FavoriteCreateParameters } from "Favorites/Common/Contracts/Favorite";
import * as Favorites_RestClient from "Favorites/Common/RestClient/Favorite";
import { IVssPageContext } from "VSS/Platform/Context";
import { ITfsPageService, IProjectData } from "Tfs/Platform/Page";


export class TestPlanFavoritesSource {

    public static getInstance() {
        if (!TestPlanFavoritesSource._instance) {
            TestPlanFavoritesSource._instance = new TestPlanFavoritesSource();
        }
        return TestPlanFavoritesSource._instance;
    }

    public init(context: IVssPageContext) {
        this._pageContext = context;
    }

    /**
     * Get all test plan favorites
     * @param includeExtendedDetails If true, retrieves additional metadata for each favorite. 
     * Additional metadata includes artifactName, links, and other server side computed values. 
     * Not includingExtendedDetails may result in stale data. 
     */
    public getFavorites(includeExtendedDetails: boolean): Promise<Favorite[]> {
        return this._getFavoritesClient().getFavorites(
                        c_favoritesTypes_testPlan,
                        this._artifactScopeType,
                        this._getProjectIdFromContext(),
                        includeExtendedDetails)
                    .then(favs => favs.filter(fav => !fav.artifactIsDeleted));
    }

    /**
     * Favorites a new item
     * @param teamId
     * @param backlogId
     */
    public createFavorite(title: string, id: string): Promise<Favorite> {
        const favoriteParams = this._createNewFavoriteParams(title, id);
        return this._getFavoritesClient().createFavorite(favoriteParams);
    }

    /**
     * Removes item from favorites
     * @param favoriteId
     */
    public deleteFavorite(favoriteId: string): Promise<void> {
        return this._getFavoritesClient().deleteFavoriteById(
            favoriteId,
            c_favoritesTypes_testPlan,
            this._artifactScopeType,
            this._getProjectIdFromContext());
    }

    /**
     * Returns favorite params to use with favorites service
     * @param teamId
     * @param backlogId
     */
    private _createNewFavoriteParams(title: string, id: string): FavoriteCreateParameters {
        return {
            artifactId: id,
            artifactProperties: {},
            artifactName: title,
            testPlanId: id,
            artifactType: c_favoritesTypes_testPlan,
            artifactScope: {
                id: this._getProjectIdFromContext(),
                type: this._artifactScopeType,
                name: this._getProjectNameFromContext()
            }
        } as FavoriteCreateParameters;
    }

    /**
     * Get favorites client
     */
    private _getFavoritesClient(): Favorites_RestClient.IFavoriteRestClient {
        return this._pageContext.getRestClient<Favorites_RestClient.IFavoriteRestClient>("IFavoriteRestClient");        
    }

    /**
     * Get project id from context
     */
    private _getProjectIdFromContext(): string {
        let project: IProjectData | null = this._getProject();
        return project ? project.id : "";
    }

    private _getProjectNameFromContext(): string {
        let project: IProjectData | null = this._getProject();
        return project ? project.name : "";
    }

    private _getProject(): IProjectData | null {
        const tfsPageData = this._pageContext.getService<ITfsPageService>("ITfsPageService").getData();
        if (tfsPageData && tfsPageData.project) {
            const project = tfsPageData.project;
            return project;
        }
        else {
            return null;
        }
    }

    private _pageContext: IVssPageContext;
    private static _instance: TestPlanFavoritesSource;
    private readonly _artifactScopeType: string = "Project";

}
const c_favoritesTypes_testPlan = "Microsoft.TeamFoundation.TestManagement.Plan";
